import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './FileUpload.module.css';

const FileUpload = ({
  onFileSelect,
  acceptedTypes = '.csv',
  disabled = false,
  maxSize = 5 * 1024 * 1024,
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (disabled) return;

    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    // Validate file type
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const allowedExtensions = acceptedTypes
      .replace(/\./g, '')
      .split(',')
      .map((ext) => ext.trim());

    if (!allowedExtensions.includes(fileExtension)) {
      alert(`Invalid file type. Please select a ${acceptedTypes} file.`);
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      alert(
        `File size too large. Maximum size is ${Math.round(maxSize / (1024 * 1024))}MB.`
      );
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const onButtonClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const clearFile = () => {
    setSelectedFile(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={styles.fileUploadContainer}>
      <div
        className={`${styles.fileUploadArea} ${dragActive ? styles.dragActive : ''} ${disabled ? styles.disabled : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={onButtonClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          className={styles.fileInput}
          onChange={handleChange}
          accept={acceptedTypes}
          disabled={disabled}
        />

        {selectedFile ? (
          <div className={styles.selectedFile}>
            <div className={styles.fileInfo}>
              <div className={styles.fileName}>📄 {selectedFile.name}</div>
              <div className={styles.fileSize}>
                {(selectedFile.size / 1024).toFixed(1)} KB
              </div>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFile();
                }}
                className={styles.clearButton}
              >
                ✕
              </button>
            )}
          </div>
        ) : (
          <div className={styles.uploadPrompt}>
            <div className={styles.uploadIcon}>📁</div>
            <div className={styles.uploadText}>
              <strong>Click to select</strong> or drag and drop your CSV file
              here
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

FileUpload.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  acceptedTypes: PropTypes.string,
  disabled: PropTypes.bool,
  maxSize: PropTypes.number,
};

export default FileUpload;
